import { sortBy } from "lodash"
import { observable } from "mobx"
import { prettyJson } from "../../common/CCUtils"
import {
    FBTimestampNow,
    FbUtils,
    QueryDocumentSnapshot,
    studentResultsCollection,
} from "../../common/FirebaseConfig"
import { log } from "../../common/Logging"
import { EveryCigBodyPart } from "../../phaser/activities/every-cig/EveryCigTypes"
import { KidsChoiceSelections } from "../../phaser/activities/kids-choice/KidsChoiceTypes"
import { Score } from "./Score"
import { FirebaseScore, StudentResults } from "./StudentResults"
import { StudentStoreSA } from "./StudentStoreSA"

export enum GameWithScore {
    ADDICTION_PONG,
    SUPER_HERO,
    WHEEL_OF_MISFORTUNE,
    ADDICTION_ROAD,
}

export class StudentResultsStore {
    private static innerInstance: StudentResultsStore

    /**
     * StudentId to StudentResult
     */
    @observable
    studentResults: Map<string, StudentResults> = new Map()

    private unsubscribers: (() => void)[] = []

    static get instance() {
        return this.innerInstance || (this.innerInstance = new this())
    }

    listenForStudentResultChanges = async (classId: string) => {
        const allStudentResults = await studentResultsCollection()
            .where("classId", "==", classId)
            .get()

        log.debug(
            "Got all the student results currently available. Listening to each individually.",
        )

        this.stopListeningForStudentResultChanges()
        return allStudentResults.docs.forEach((doc) => {
            this.addStudentResultsDoc(doc)
            const unsubscribe = studentResultsCollection()
                .doc(doc.id)
                .onSnapshot(
                    (studentResultsDoc) => {
                        this.addStudentResultsDoc(studentResultsDoc)
                    },
                    (err) => {
                        log.debug(err)
                    },
                )
            this.unsubscribers.push(unsubscribe)
        })
    }

    stopListeningForStudentResultChanges = () => {
        this.unsubscribers.forEach((unsubscribe) => unsubscribe())
        this.unsubscribers = []
    }

    addScore = async (game: GameWithScore, score: number) => {
        const dbScore = {
            score,
            date: FBTimestampNow(),
        }
        let update: Partial<StudentResults>
        switch (game) {
            case GameWithScore.ADDICTION_PONG:
                update = {
                    addictionPongScore: dbScore,
                }
                break
            case GameWithScore.SUPER_HERO:
                update = {
                    superHeroScore: dbScore,
                }
                break
            case GameWithScore.WHEEL_OF_MISFORTUNE:
                update = {
                    wheelOfMisfortuneScore: dbScore,
                }
                break
            case GameWithScore.ADDICTION_ROAD:
                update = {
                    addictionRoadScore: dbScore,
                }
                break
            default:
                throw new Error(`No case for ${game}`)
        }
        await this.update(update)
    }

    makeEveryCigChoice = (everyCigChoice: EveryCigBodyPart) => {
        const update: Partial<StudentResults> = {
            everyCigChoice,
        }
        this.update(update)
    }

    saveKidsChoiceChoice = (kidsChoiceSelections: KidsChoiceSelections) => {
        const update: Partial<StudentResults> = {
            kidsChoiceSelections,
        }
        this.update(update)
    }

    findKidsChoiceSelections = () =>
        this.studentResultsArray
            .map((results) => results.kidsChoiceSelections)
            .filter((result) => result)

    findEveryCigChoices = (): EveryCigBodyPart[] => {
        return this.studentResultsArray
            .map((results) => results.everyCigChoice)
            .filter((bodyPart) => bodyPart != null)
    }

    /**
     * Must already be listening for StudentResult changes. Returns scores sorted from most recent to least.
     */
    findRecentScores = (game: GameWithScore, includeUser = false): Score[] => {
        const scores: Score[] = this.studentResultsArray
            .map((entry) => {
                let score: FirebaseScore
                switch (game) {
                    case GameWithScore.ADDICTION_PONG:
                        score = entry.addictionPongScore
                        break
                    case GameWithScore.SUPER_HERO:
                        score = entry.superHeroScore
                        break
                    case GameWithScore.WHEEL_OF_MISFORTUNE:
                        score = entry.wheelOfMisfortuneScore
                        break
                    case GameWithScore.ADDICTION_ROAD:
                        score = entry.addictionRoadScore
                        break
                    default:
                        throw new Error(`No case for ${game}`)
                }
                if (score == null) {
                    return undefined
                }
                return {
                    score: score.score,
                    screenname: entry.screenname,
                    date: score.date.toDate(),
                }
            })
            .filter((score) => score != null)
        const sorted = sortBy(scores, (score: Score) => score.date).reverse()
        log.debug("Scores sorted into scores by date " + prettyJson(sorted))
        return includeUser
            ? sorted
            : sorted.filter(
                  (score) =>
                      score.screenname !==
                      StudentStoreSA.instance.findUsername(),
              )
    }

    update = (update: Partial<StudentResults>) =>
        studentResultsCollection()
            .doc(StudentStoreSA.instance.student.id)
            .update(update)

    private addStudentResultsDoc = (
        studentResultsDoc: QueryDocumentSnapshot,
    ) => {
        const studentResults = FbUtils.queryDocToDoc(
            studentResultsDoc,
        ) as StudentResults
        // log.debug(`Add student results to map ${prettyJson(studentResults)}`)
        this.studentResults.set(studentResults.id, studentResults)
    }

    get studentResultsArray(): StudentResults[] {
        return Array.from(this.studentResults, ([, value]) => value)
    }

    get currentStudentResults(): StudentResults {
        log.debug(
            `Current student results: ${prettyJson(
                this.studentResults.get(StudentStoreSA.instance.student.id),
            )}`,
        )
        return this.studentResults.get(StudentStoreSA.instance.student.id)
    }
}
